import React, { useEffect } from 'react';
import classPrefix from 'prefix-classnames';
import { Steps } from 'antd';
import { useSelector, useIntl, useDispatch } from 'umi';
import { usePersistFn } from 'ahooks';
import { getDefaultBreadcrumbItems, stepInfo } from '@/utils/ServiceConf';
import FooterButton from '@/components/FooterButton';
import type { StepModelsState } from 'umi';
import GuideButton from '@/components/GuideButton';
import Breadcrumb from '@/components/Breadcrumb';
import type { StepInfoType } from './types/defs';
import StepRender from './components/StepRender';
import useHandleStep from './components/StepRender/HandleStep';

import './WebServiceStep.less';

const px = classPrefix('web-service-step');

const { Step } = Steps;

function WebServiceStep() {
  const dispatch = useDispatch();
  // 獲取國際化配置對象
  const intl = useIntl();

  // 從全局狀態中獲取數據
  const { step, maxStep } = useSelector((x: { stepModels: StepModelsState }) => x.stepModels);

  // 當前步驟
  const currentStep = stepInfo[step - 1];

  // 更新/初始化models
  const updataModels = usePersistFn((payload) => {
    dispatch({
      type: 'stepModels/update',
      payload,
    });
  });

  useEffect(() => {
    // 记录当前到过的最大步骤数，用于步骤间跳转
    if (maxStep < step) {
      updataModels({ maxStep: step });
    }
  }, [step]);

  /**
   * 上一步按鈕點擊
   */
  const handlePrevStep = usePersistFn(async () => {
    updataModels({ step: step - 1 });
  });

  /**
   * 下一步按鈕點擊
   */
  const handleNextStep = useHandleStep(currentStep);

  /**
   * 计算步骤条状态
   * @var step 当前步骤 从1开始
   * @var maxStep 最大到达步骤 从1开始
   * @param index 索引 从0开始
   */
  const getStepStatus = usePersistFn((index: number) => {
    if (index + 1 === step) {
      return 'process';
    }
    return undefined;
  });

  return (
    <>
      <div className={px('root')}>
        {/* 面包屑 */}
        <Breadcrumb itemList={getDefaultBreadcrumbItems()} />
        {/* 左上角標題 */}
        <h2 className={px('title')}>{intl.formatMessage({ id: 'Demo事项' })}</h2>
        <div className={px('stepinfo')}>
          {/* 左側步驟條 */}
          <div className={px('step')}>
            <h3>{intl.formatMessage({ id: '申请步骤' })}</h3>
            <Steps direction="vertical" size="small" current={step - 1}>
              {stepInfo.map((info: StepInfoType, index) => (
                <Step
                  key={info.stepType}
                  title={intl.formatMessage({ id: info.stepName })}
                  icon={index + 1}
                  status={getStepStatus(index)}
                />
              ))}
            </Steps>
          </div>
          {/* 內容區標題 */}
          <div className={px('form')}>
            <div className={px('formTitle')}>
              {currentStep?.stepName ? intl.formatMessage({ id: currentStep.stepName }) : ''}
            </div>
            {/* 主要內容區 */}
            <StepRender
              currentStep={currentStep}
              stepInfoList={stepInfo}
              handleNext={handleNextStep}
              mobile={false}
            />
          </div>
        </div>
      </div>
      {/* 右側的浮動圖標按鈕 */}
      <GuideButton />
      {/* 底部的按鈕 */}
      <FooterButton
        className={px('footer-button')}
        step={step}
        stepType={currentStep?.stepType}
        handleNextStep={handleNextStep}
        handlePrevStep={handlePrevStep}
      />
    </>
  );
}

export default WebServiceStep;
